/**
 * ProjectChip — chip-strip project selector.
 *
 * Refactored from HeaderProjectSwitcher's project half. Preserves all data
 * flow back to App.js: user picks a project_id, we resolve to the full
 * project object and call onProjectChange. The `data-tour="project-switcher"`
 * anchor stays on the outer wrapper so existing tours still resolve.
 */
import React from 'react';
import {
  Box,
  Chip,
  Divider,
  FormControl,
  ListSubheader,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { FolderOpen } from '@mui/icons-material';

const ProjectChip = ({
  projects = [],
  selectedProject,
  onProjectChange,
  recentProjectIds = [],
}) => {
  const recentProjects = recentProjectIds
    .map((id) => projects.find((p) => p.project_id === id))
    .filter(Boolean);

  const otherProjects = projects.filter(
    (p) => !recentProjectIds.includes(p.project_id),
  );

  const handleSelect = (event) => {
    const projectId = event.target.value;
    if (projectId === '') {
      onProjectChange(null);
    } else {
      const project = projects.find((p) => p.project_id === projectId);
      onProjectChange(project);
    }
  };

  return (
    <Box
      data-tour="project-switcher"
      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
    >
      <FolderOpen sx={{ color: '#667eea', fontSize: 16 }} />
      <FormControl size="small">
        <Select
          value={selectedProject?.project_id || ''}
          onChange={handleSelect}
          displayEmpty
          sx={{
            minWidth: 160,
            bgcolor: 'background.paper',
            '& .MuiSelect-select': { py: 0.5, fontSize: '0.8125rem' },
          }}
          MenuProps={{
            PaperProps: { sx: { maxHeight: 400, mt: 0.5 } },
          }}
        >
          <MenuItem value="">
            <Typography
              color="text.secondary"
              sx={{ fontStyle: 'italic', fontSize: '0.8125rem' }}
            >
              Select Project
            </Typography>
          </MenuItem>

          {recentProjects.length > 0 && (
            <ListSubheader
              sx={{
                bgcolor: 'background.paper',
                lineHeight: '32px',
                fontSize: '0.7rem',
                fontWeight: 600,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Recent
            </ListSubheader>
          )}

          {recentProjects.map((project) => (
            <MenuItem
              key={`recent-${project.project_id}`}
              value={project.project_id}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '0.8125rem' }}>
                  {project.name}
                </Typography>
                <Chip
                  label="recent"
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.6rem',
                    bgcolor: 'rgba(102, 126, 234, 0.1)',
                    color: '#667eea',
                  }}
                />
              </Box>
            </MenuItem>
          ))}

          {recentProjects.length > 0 && otherProjects.length > 0 && (
            <Divider sx={{ my: 0.5 }} />
          )}

          {otherProjects.length > 0 && (
            <ListSubheader
              sx={{
                bgcolor: 'background.paper',
                lineHeight: '32px',
                fontSize: '0.7rem',
                fontWeight: 600,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              All Projects
            </ListSubheader>
          )}

          {otherProjects.map((project) => (
            <MenuItem
              key={project.project_id}
              value={project.project_id}
              sx={{ fontSize: '0.8125rem' }}
            >
              {project.name}
            </MenuItem>
          ))}

          {projects.length === 0 && (
            <MenuItem disabled>
              <Typography
                color="text.secondary"
                sx={{ fontSize: '0.8125rem' }}
              >
                No projects available
              </Typography>
            </MenuItem>
          )}
        </Select>
      </FormControl>
    </Box>
  );
};

export default ProjectChip;
